import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Chip,
    Collapse,
    TextField,
    alpha,
    useTheme
} from '@mui/material';
import {
    ExpandMore,
    ExpandLess,
    CheckCircle,
    Error,
    Warning,
    Delete,
    Refresh,
    Visibility,
    Edit
} from '@mui/icons-material';
import { MaterialReactTable } from "material-react-table";
import {
    groupItemsByCounterparty,
    getItemStatus,
    getItemRowStyles,
    getStatusText,
    getReceiptStats,
    canSendReceipt
} from '../utils/receiptUtils';
import { formatPrice, formatDiscount, getDiscountColors } from '../utils/currencyUtils';
import { useTranslation } from 'react-i18next';

/**
 * Компонент для отображения товаров в приемке, сгруппированных по контрагентам
 */
export default function GroupedReceiptItems({
    items,
    sendResults = [],
    onQuantityChange,
    onNoteChange,
    onRemoveItem,
    onSendGroup,
    onViewInMoysklad,
    disabled = false,
    showActions = true,
    counterparties = [],
    receiptStatus = 'draft'
}) {
    const { t } = useTranslation();
    const theme = useTheme();
    const [expandedGroups, setExpandedGroups] = useState(() => {
        // При инициализации создаем пустое множество - группы будут добавляться по мере появления
        return new Set();
    });
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, group: null });

    // Состояния для модального окна примечаний
    const [noteDialog, setNoteDialog] = useState({ open: false, itemUniqueId: null, itemName: '', currentNote: '' });
    const [tempNote, setTempNote] = useState('');

    // Цвета для визуализации коробок
    const PRODUCT_COLORS = [
        { bg: '#e3f2fd', border: '#90caf9' },
        { bg: '#f3e5f5', border: '#ce93d8' },
        { bg: '#e8f5e9', border: '#a5d6a7' },
        { bg: '#fff3e0', border: '#ffcc80' },
        { bg: '#fce4ec', border: '#f48fb1' },
        { bg: '#e0f7fa', border: '#80deea' },
    ];

    const productColorMap = useMemo(() => {
        const map = new Map();
        let idx = 0;
        items.forEach(item => {
            const key = `${item.barcode}_${item.purchaseOrderId}`;
            if (!map.has(key)) {
                map.set(key, idx % PRODUCT_COLORS.length);
                idx++;
            }
        });
        return map;
    }, [items]);

    // Группируем товары по контрагентам
    const groupedItems = useMemo(() => {
        return groupItemsByCounterparty(items, sendResults, counterparties);
    }, [items, sendResults, counterparties]);

    // Используем товары напрямую, так как у каждого есть uniqueId
    const groupedItemsWithIds = useMemo(() => {
        return groupedItems.map(group => ({
            ...group,
            items: group.items
        }));
    }, [groupedItems]);

    // Отслеживаем количество товаров в каждой группе для точного определения изменений
    const prevGroupItemsCountRef = useRef(new Map());

    // Умное разворачивание только измененных групп
    useEffect(() => {
        // Создаем Map с текущим количеством товаров по группам
        const currentGroupItemsCount = new Map();
        groupedItems.forEach(group => {
            const groupId = group.purchaseOrderId || 'no_order';
            currentGroupItemsCount.set(groupId, group.items.length);
        });

        const prevGroupItemsCount = prevGroupItemsCountRef.current;

        setExpandedGroups(prev => {
            const newExpanded = new Set(prev);

            // Проверяем каждую группу на предмет изменений
            currentGroupItemsCount.forEach((currentCount, groupId) => {
                const prevCount = prevGroupItemsCount.get(groupId) || 0;

                // Если это новая группа (не было в предыдущем состоянии) - разворачиваем
                if (!prevGroupItemsCount.has(groupId)) {
                    newExpanded.add(groupId);
                }
                // Если количество товаров в группе увеличилось - разворачиваем
                else if (currentCount > prevCount) {
                    newExpanded.add(groupId);
                }
            });

            return newExpanded;
        });

        // Сохраняем текущее состояние для следующего сравнения
        prevGroupItemsCountRef.current = currentGroupItemsCount;
    }, [groupedItems]);

    // Получаем статистику
    const stats = useMemo(() => {
        return getReceiptStats(items, sendResults);
    }, [items, sendResults]);

    // Проверяем возможность отправки
    const sendCheck = useMemo(() => {
        return canSendReceipt(items, sendResults);
    }, [items, sendResults]);

    // Переключаем развернутость группы
    const toggleGroupExpansion = useCallback((groupId) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
    }, [expandedGroups]);

    // Развернуть/свернуть все группы
    const toggleAllGroups = useCallback((expand) => {
        if (expand) {
            setExpandedGroups(new Set(groupedItemsWithIds.map(g => g.purchaseOrderId || 'no_order')));
        } else {
            setExpandedGroups(new Set());
        }
    }, [groupedItemsWithIds]);

    // Получаем иконку статуса
    const getStatusIcon = (status) => {
        switch (status) {
            case 'success': return <CheckCircle color="success" />;
            case 'error': return <Error color="error" />;
            case 'pending': return <Warning color="warning" />;
            default: return null;
        }
    };

    // Получаем цвет статуса
    const getStatusColor = (status) => {
        switch (status) {
            case 'success': return 'success';
            case 'error': return 'error';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    // Обработчики действий с группами
    const handleSendGroup = useCallback((group) => {
        if (onSendGroup) {
            onSendGroup(group);
        }
    }, [onSendGroup]);

    const handleViewInMoysklad = useCallback((group) => {
        if (onViewInMoysklad && group.result?.moyskladId) {
            onViewInMoysklad(group);
        }
    }, [onViewInMoysklad]);

    const handleRetryGroup = useCallback((group) => {
        setConfirmDialog({
            open: true,
            action: 'retry',
            group: group
        });
    }, []);

    const confirmRetryGroup = useCallback(() => {
        if (confirmDialog.action === 'retry' && confirmDialog.group) {
            handleSendGroup(confirmDialog.group);
        }
        setConfirmDialog({ open: false, action: null, group: null });
    }, [confirmDialog, handleSendGroup]);

    // Обработчики для модального окна примечаний
    const handleOpenNoteDialog = useCallback((uniqueId, itemName, currentNote) => {
        setNoteDialog({
            open: true,
            itemUniqueId: uniqueId,
            itemName,
            currentNote: currentNote || ''
        });
        setTempNote(currentNote || '');
    }, []);

    const handleCloseNoteDialog = () => {
        setNoteDialog({ open: false, itemUniqueId: null, itemName: '', currentNote: '' });
        setTempNote('');
    };

    const handleSaveNote = () => {
        onNoteChange(noteDialog.itemUniqueId, tempNote === '' ? '' : tempNote);
        handleCloseNoteDialog();
    };

    const handleRemoveNote = () => {
        onNoteChange(noteDialog.itemUniqueId, null);
        handleCloseNoteDialog();
    };

    // Колонки таблицы товаров
    const tableColumns = useMemo(
        () => [
            {
                accessorKey: 'miniature',
                header: t('label.photo'),
                size: 100,
                Cell: ({ cell }) => (
                    <Box display="flex" justifyContent="center">
                        {cell.getValue() ? (
                            <img
                                src={cell.getValue()}
                                alt="Item"
                                style={{
                                    width: '60px',
                                    height: '60px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd'
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: '60px',
                                    height: '60px',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '8px',
                                    border: '1px solid #ddd',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '10px',
                                    color: '#999'
                                }}
                            >
                                {t('msg.noPhoto')}
                            </Box>
                        )}
                    </Box>
                )
            },
            {
                accessorKey: 'boxNumber',
                header: t('label.box'),
                size: 60,
                Cell: ({ cell, row }) => {
                    const colorIdx = productColorMap.get(`${row.original.barcode}_${row.original.purchaseOrderId}`);
                    const colors = PRODUCT_COLORS[colorIdx !== undefined ? colorIdx : 0];
                    return (
                        <Box sx={{
                            border: `2px solid ${colors.border}`,
                            borderRadius: 1,
                            p: 0.5,
                            textAlign: 'center',
                            backgroundColor: colors.bg,
                            minWidth: '32px',
                            fontWeight: 600,
                            fontSize: '14px'
                        }}>
                            {cell.getValue() || 1}
                        </Box>
                    );
                }
            },
            {
                accessorKey: 'name',
                header: t('label.name'),
                size: 250,
                Cell: ({ row }) => {
                    const item = row.original;
                    const hasConsignment = item.consignmentId && item.consignmentLabel;

                    return (
                        <div>
                            <div>{item.name}</div>
                            {hasConsignment && (
                                <div style={{
                                    display: 'inline-block',
                                    marginTop: '4px',
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    color: '#1565c0',
                                    backgroundColor: '#e3f2fd',
                                    border: '1px solid #90caf9',
                                    borderRadius: '4px'
                                }}>
                                    {t('label.series')}: {item.consignmentLabel}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'article',
                header: t('label.sku'),
                size: 120
            },
            {
                accessorKey: 'price',
                header: t('label.price'),
                size: 120,
                Cell: ({ row }) => {
                    const item = row.original;
                    return (
                        <div style={{ fontSize: '14px', fontWeight: 500 }}>
                            {formatPrice(item.price, item.currency)}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'discount',
                header: t('label.discount'),
                size: 100,
                Cell: ({ row }) => {
                    const item = row.original;
                    const discountColors = getDiscountColors(item.discount);

                    return (
                        <div style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: discountColors.color,
                            textAlign: 'left'
                        }}>
                            {formatDiscount(item.discount)}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'quantity',
                header: t('label.quantity'),
                size: 120,
                Cell: ({ row, cell }) => {
                    const item = row.original;
                    const itemStatus = getItemStatus(item, sendResults);

                    return (
                        <Box
                            sx={{
                                opacity: itemStatus.allowEdit ? 1 : 0.6,
                                pointerEvents: itemStatus.allowEdit ? 'auto' : 'none'
                            }}
                        >
                            {onQuantityChange ? (() => {
                                const value = cell.getValue();
                                const isQuantityChanged = row.original.originalQuantity && value !== row.original.originalQuantity;

                                return (
                                    <input
                                        type="number"
                                        value={value}
                                        onChange={(e) => {
                                            const newValue = parseInt(e.target.value) || 0;
                                            if (newValue >= 0) {
                                                onQuantityChange(row.original.uniqueId, newValue);
                                            }
                                        }}
                                        disabled={!itemStatus.allowEdit || disabled}
                                        style={{
                                            width: '60px',
                                            padding: '4px',
                                            border: `1px solid ${isQuantityChanged ? '#ff9800' : '#ddd'}`,
                                            borderWidth: isQuantityChanged ? '2px' : '1px',
                                            borderRadius: '4px',
                                            textAlign: 'center',
                                            fontWeight: isQuantityChanged ? 'bold' : 'normal',
                                            color: isQuantityChanged ? '#f24405' : 'inherit',
                                            backgroundColor: isQuantityChanged ? '#fff3e0' : (itemStatus.allowEdit ? 'white' : '#f5f5f5')
                                        }}
                                    />
                                );
                            })() : (
                                cell.getValue()
                            )}
                        </Box>
                    );
                }
            },
            {
                accessorKey: 'note',
                header: t('label.note'),
                size: 120,
                Cell: ({ cell, row }) => {
                    const item = row.original;
                    const itemStatus = getItemStatus(item, sendResults);

                    return (
                        <Box
                            sx={{
                                opacity: itemStatus.allowEdit ? 1 : 0.6,
                                pointerEvents: itemStatus.allowEdit ? 'auto' : 'none'
                            }}
                        >
                            {onNoteChange ? (() => {
                                const noteValue = cell.getValue();
                                const hasNote = noteValue !== null && noteValue !== undefined;

                                return (
                                    <div
                                        onClick={() => handleOpenNoteDialog(row.original.uniqueId, row.original.name, noteValue)}
                                        style={{
                                            cursor: 'pointer',
                                            padding: '5px',
                                            borderRadius: '4px',
                                            backgroundColor: hasNote ? '#ffebee' : 'transparent',
                                            color: hasNote ? '#d32f2f' : '#666',
                                            fontSize: '12px',
                                            textAlign: 'center',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '4px',
                                            minHeight: '32px',
                                            border: '1px solid #ddd',
                                            transition: 'all 0.2s ease'
                                        }}
                                        title={hasNote ? (noteValue === '' ? t('msg.marked') : noteValue) : t('msg.addNote')}
                                    >
                                        {hasNote && <span>🔴</span>}
                                        {!hasNote ? '—' : (noteValue === '' ? t('msg.marked') : t('msg.hasNote'))}
                                    </div>
                                );
                            })() : (
                                (() => {
                                    const noteValue = cell.getValue();
                                    const hasNote = noteValue !== null && noteValue !== undefined;
                                    return (
                                        <div style={{ fontSize: '12px', color: hasNote ? '#d32f2f' : '#666' }}>
                                            {!hasNote ? '—' : (noteValue === '' ? t('msg.marked') : noteValue)}
                                        </div>
                                    );
                                })()
                            )}
                        </Box>
                    );
                }
            },
            ...(onRemoveItem && !disabled ? [{
                accessorKey: 'actions',
                header: t('label.actions'),
                size: 80,
                enableSorting: false,
                Cell: ({ row }) => {
                    const item = row.original;
                    const itemStatus = getItemStatus(item, sendResults);

                    if (!itemStatus.allowDelete) {
                        return null;
                    }

                    return (
                        <IconButton
                            onClick={() => onRemoveItem(row.original.uniqueId)}
                            color="error"
                            size="small"
                            disabled={disabled}
                        >
                            <Delete />
                        </IconButton>
                    );
                }
            }] : [])
        ],
        [onQuantityChange, onNoteChange, onRemoveItem, sendResults, disabled, productColorMap]
    );

    return (
        <Box>
            {/* Компактная статистика */}
            <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                    {t('msg.receipt')}
                </Typography>
                <Chip size="small" label={t('msg.groups', { count: stats.totalGroups })} variant="outlined" />
                <Chip size="small" color="success" label={`✓ ${stats.successGroups}`} variant={stats.successGroups > 0 ? "filled" : "outlined"} />
                <Chip size="small" color="error" label={`✗ ${stats.errorGroups}`} variant={stats.errorGroups > 0 ? "filled" : "outlined"} />
                <Chip size="small" color="warning" label={`⏳ ${stats.pendingGroups}`} variant={stats.pendingGroups > 0 ? "filled" : "outlined"} />
            </Box>

            {/* Кнопки управления - убраны для автоматического разворачивания */}

            {/* Сообщение о невозможности отправки */}
            {!sendCheck.canSend && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {sendCheck.reason}
                    {sendCheck.itemsToRetry > 0 && (
                        <Box mt={1}>
                            <Typography variant="body2">
                                {t('msg.retrySendingFor', { count: sendCheck.itemsToRetry })}
                            </Typography>
                        </Box>
                    )}
                </Alert>
            )}

            {/* Группы товаров */}
            {groupedItemsWithIds.map((group) => {
                const groupId = group.purchaseOrderId || 'no_order';
                const isExpanded = expandedGroups.has(groupId);

                return (
                    <Paper
                        key={groupId}
                        sx={{
                            mb: 1,
                            p: 1,
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            '&:hover': { borderColor: '#d0d0d0' }
                        }}
                    >
                        {/* Заголовок группы */}
                        <Box
                            sx={{
                                p: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                backgroundColor: alpha(theme.palette.primary.main, 0.03),
                                borderRadius: 1,
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.06)
                                }
                            }}
                            onClick={() => toggleGroupExpansion(groupId)}
                        >
                            <Box display="flex" alignItems="center" gap={2}>
                                {getStatusIcon(group.status)}
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                    📦 {group.agentName} ({group.purchaseOrderName})
                                </Typography>
                                <Chip
                                    label={t('msg.groups', { count: group.items.length })}
                                    size="small"
                                    color={getStatusColor(group.status)}
                                />
                            </Box>

                            <Box display="flex" alignItems="center" gap={1}>
                                {/* Кнопки отправки (только для черновиков) */}
                                {receiptStatus === 'draft' && showActions && (
                                    <>
                                        {group.status === 'pending' && onSendGroup && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                color="primary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSendGroup(group);
                                                }}
                                                disabled={disabled}
                                            >
                                                {t('btn.send')}
                                            </Button>
                                        )}

                                        {group.status === 'error' && onSendGroup && (
                                            <Button
                                                variant="contained"
                                                size="small"
                                                color="warning"
                                                startIcon={<Refresh />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRetryGroup(group);
                                                }}
                                                disabled={disabled}
                                            >
                                                {t('btn.retry')}
                                            </Button>
                                        )}
                                    </>
                                )}

                                {/* Кнопка просмотра в МойСклад (всегда для успешно отправленных) */}
                                {group.status === 'success' && onViewInMoysklad && group.result?.moyskladId && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<Visibility />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewInMoysklad(group);
                                        }}
                                    >
                                        {t('msg.viewInMoysklad')}
                                    </Button>
                                )}

                                <IconButton size="small">
                                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Таблица товаров группы */}
                        <Collapse in={isExpanded}>
                            {group.status === 'error' && group.result?.error && (
                                <Alert severity="error" sx={{ mb: 1, mx: 1 }}>
                                    <Typography variant="body2">
                                        {getStatusText(getItemStatus(group.items[0], sendResults))}
                                    </Typography>
                                </Alert>
                            )}

                            <MaterialReactTable
                                columns={tableColumns}
                                data={group.items}
                                getRowId={(row) => row.uniqueId}
                                enablePagination={false}
                                enableTopToolbar={false}
                                enableBottomToolbar={false}
                                enableSorting={false}
                                enableColumnFilters={false}
                                enableColumnActions={false}
                                enableDensityToggle={false}
                                enableFullScreenToggle={false}
                                enableHiding={false}
                                muiTableContainerProps={{
                                    sx: {
                                        boxShadow: 'none',
                                        border: 'none'
                                    }
                                }}
                                muiTableBodyRowProps={({ row }) => {
                                    const itemStatus = getItemStatus(row.original, sendResults);
                                    return {
                                        sx: getItemRowStyles(itemStatus)
                                    };
                                }}
                            />
                        </Collapse>
                    </Paper>
                );
            })}

            {/* Диалог подтверждения повторной отправки */}
            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, action: null, group: null })}>
                <DialogTitle>{t('msg.confirmRetryTitle')}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('msg.confirmRetry', { agentName: confirmDialog.group?.agentName })}
                    </Typography>
                    {confirmDialog.group && (
                        <Box mt={2}>
                            <Typography variant="body2" color="text.secondary">
                                {t('msg.orderLabel')}: {confirmDialog.group.purchaseOrderName}<br/>
                                {t('msg.itemsLabel')}: {confirmDialog.group.items.length}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false, action: null, group: null })}>
                        {t('btn.cancel')}
                    </Button>
                    <Button onClick={confirmRetryGroup} variant="contained" color="warning">
                        {t('msg.retrySend')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог примечания */}
            <Dialog open={noteDialog.open} onClose={handleCloseNoteDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{t('msg.itemNote')}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1 }}>
                        <strong>{noteDialog.itemName}</strong>
                    </Typography>
                    <TextField
                        autoFocus
                        label={t('label.noteOptional')}
                        multiline
                        rows={3}
                        value={tempNote}
                        onChange={(e) => setTempNote(e.target.value)}
                        placeholder={t('placeholder.notePlaceholder')}
                        fullWidth
                        variant="outlined"
                        margin="normal"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: '#f24405',
                                    borderWidth: '2px'
                                }
                            },
                            '& .MuiInputLabel-root': {
                                '&.Mui-focused': {
                                    color: '#f24405'
                                }
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCloseNoteDialog}
                        sx={{
                            color: '#757575',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                    >
                        {t('btn.cancel')}
                    </Button>
                    {noteDialog.currentNote !== null && (
                        <Button onClick={handleRemoveNote} color="error">
                            {t('btn.removeMark')}
                        </Button>
                    )}
                    <Button
                        onClick={handleSaveNote}
                        variant="contained"
                        sx={{
                            backgroundColor: '#f24405',
                            '&:hover': { backgroundColor: '#a62f04' }
                        }}
                    >
                        {t('btn.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}